import React, {useState} from 'react'

// Chakra Components
import {Accordion, Box} from '@salesforce/retail-react-app/app/components/shared/ui'

// Constants
import {CART_PDC_PAGE_ID} from '../../../constants'

// Utilities
import {usePage} from '@salesforce/commerce-sdk-react'
import {PageContext, Component} from '@salesforce/commerce-sdk-react/components'
import {PDCDeliveryMethodOrReturn} from '../../../page-designer/assets/pdc-delivery-method-or-return'
import {PDCSecurePaymentsIcon} from '../../../page-designer/assets/pdc-secure-payments-icon'
import {PDCDeliveryMethodsAndReturns} from '../../../page-designer/layouts/pdc-delivery-methods-and-returns'
import {PDCSecurePayments} from '../../../page-designer/layouts/pdc-secure-payments'
import {usePageParameters} from '@app/hooks/use-page-parameters'
import useSiteType from '@app/hooks/use-site-type'

const PAGEDESIGNER_TO_COMPONENT = {
    'commerce_assets.pdcDeliveryMethodOrReturn': PDCDeliveryMethodOrReturn,
    'commerce_layouts.pdcDeliveryMethodsAndReturns': PDCDeliveryMethodsAndReturns,
    'commerce_assets.pdcSecurePaymentsIcon': PDCSecurePaymentsIcon,
    'commerce_layouts.pdcSecurePayments': PDCSecurePayments
}

/**
 * CartInformativeParts is a React functional component that renders a list of informative parts
 * within a shopping cart page. It utilizes the Page Designer components to dynamically display
 * content based on the configuration of the cart page.
 *
 * @param {Object} props - The properties passed to the component.
 * @returns {JSX.Element} A Box component containing an Accordion of informative parts, each wrapped
 *                        in a PageContext.Provider to supply necessary context for rendering.
 *
 * The component fetches the cart page data using the `usePage` hook, filtering the regions to find
 * those with the ID 'informativeParts'. It then maps over these components, rendering each within
 * an Accordion list item. The `PAGEDESIGNER_TO_COMPONENT` mapping is used to determine which
 * component to render for each part.
 *
 * The component ensures that the list of informative parts is only rendered if there are any
 * components available. Each component is wrapped in a `PageContext.Provider` to provide the
 * necessary context for rendering the Page Designer components.
 */
const CartInformativeParts = ({...props}) => {
    const {isOutlet} = useSiteType()
    const parameters = usePageParameters(CART_PDC_PAGE_ID + (isOutlet ? '-outlet' : ''), {includeCountry: true})
    const {data: cartPage} = usePage({parameters})
    const cartInformativeParts = cartPage?.regions?.filter(
        (region) => region.id === 'informativeParts'
    )[0]?.components
    const [contextValue] = useState({components: PAGEDESIGNER_TO_COMPONENT})

    return (
        <Box {...props}>
            {cartInformativeParts && cartInformativeParts.length > 0 && (
                <Accordion as="ul" allowMultiple="true" listStyleType="none">
                    {cartInformativeParts.map((component) => (
                        <li key={component.id}>
                            <PageContext.Provider value={contextValue}>
                                <Component component={component} />
                            </PageContext.Provider>
                        </li>
                    ))}
                </Accordion>
            )}
        </Box>
    )
}

export default CartInformativeParts
